import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import EditTemplatePage from '../../EditTemplatePage';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import useBrands from '../../hooks/useBrands';
import useMarkets from '../../hooks/useMarkets';
import {
  editTemplate,
  getTemplateById,
} from '../../hooks/useTemplates';
import { normalizeQrMarginsForSave, validateTemplateForSave } from '../../wysiwyg/templateNormalization';
import { getErrorBody, getErrorMessage } from '../../utils/requestErrors';

export default function EditTemplateContainer() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { org } = useLegacyAppSession();
  const templateId = String(params.templateId || '');
  const [fallbackTemplate, setFallbackTemplate] = useState(location.state?.template || null);
  const [isLoading, setIsLoading] = useState(!location.state?.template);
  const {
    data: organizationMarkets,
    error: organizationMarketsError,
    isLoading: areOrganizationMarketsLoading,
  } = useMarkets({
    enabled: Boolean(org?.id),
    organizationId: org?.id,
    page: 0,
    pageSize: 200,
    scope: 'organization',
  });
  const { data: availableMarkets, error: availableMarketsError } = useMarkets({
    enabled: Boolean(org?.id),
    organizationId: org?.id,
    scope: 'all',
  });
  const { data: organizationBrands, error: organizationBrandsError } = useBrands({
    enabled: Boolean(org?.id),
    organizationId: org?.id,
    loadAll: true,
  });

  useEffect(() => {
    if (organizationMarketsError) {
      showToastError('Template markets could not be loaded.');
    }
  }, [organizationMarketsError]);

  useEffect(() => {
    if (availableMarketsError) {
      showToastError('Market configuration could not be loaded. TAC calculation is disabled.');
    }
  }, [availableMarketsError]);

  useEffect(() => {
    if (organizationBrandsError) {
      showToastError('Template brands could not be loaded.');
    }
  }, [organizationBrandsError]);

  useEffect(() => {
    if (!templateId || !org?.id || fallbackTemplate) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getTemplateById({ organizationId: org.id, templateId, markets: [] })
      .then((template) => {
        if (cancelled) return;
        setFallbackTemplate(template);
      })
      .catch(() => {
        if (cancelled) return;
        showToastError('Template could not be loaded.');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fallbackTemplate, org?.id, templateId]);

  if (!templateId) {
    return <Navigate to="/templates" replace />;
  }

  if (isLoading || (org?.id && areOrganizationMarketsLoading)) {
    return null;
  }

  const availableBrands = (organizationBrands || [])
    .map((brand) => ({ id: Number(brand.id), name: brand.name }))
    .filter((brand) => Number.isFinite(brand.id))
    .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }));

  return (
    <EditTemplatePage
      onClose={() => navigate('/templates', { replace: true })}
      onSave={async (wysiwygTemplate, originalTemplate, marketIds) => {
        if (!org?.id) return;

        const validationErrors = validateTemplateForSave(wysiwygTemplate);
        if (validationErrors.length > 0) {
          showToastError(`Template could not be saved: ${validationErrors[0]}`);
          console.error('Template validation failed:', validationErrors);
          return;
        }

        try {
          const savedTemplate = normalizeQrMarginsForSave(wysiwygTemplate);
          await editTemplate({
            templateId: originalTemplate.id,
            name: wysiwygTemplate.name,
            content: JSON.stringify(savedTemplate),
            marketIds,
          });
          showToastSuccess('Template successfully saved.');
          navigate('/templates', { replace: true });
        } catch (error) {
          const serverErrors = (getErrorBody(error) as { errors?: string[] })?.errors;
          showToastError(
            serverErrors?.length
              ? `Template could not be saved: ${serverErrors[0]}`
              : `Template could not be saved: ${getErrorMessage(error, 'Please try again.')}`,
          );
          console.error('Failed to save template:', error);
        }
      }}
      organizationId={org?.id ? String(org.id) : undefined}
      fallbackTemplate={fallbackTemplate}
      orgMarketIds={(organizationMarkets || []).map((market) => market.id)}
      availableMarkets={availableMarkets}
      orgBrandIds={availableBrands.map((brand) => brand.id)}
      availableBrands={availableBrands}
    />
  );
}
